import { getParty } from '@val/shared'
import type { CSSProperties } from 'react'
import { int, signed } from '../lib/format.ts'
import './sim.css'

interface Props {
  votes: Record<string, number>
  baseVotes: Record<string, number>
  seats: Record<string, number>
  baseSeats: Record<string, number>
  highlightParty?: string
  onChange: (code: string, votes: number) => void
  onReset: () => void
}

export function PartyVoteEditor({
  votes,
  baseVotes,
  seats,
  baseSeats,
  highlightParty,
  onChange,
  onReset,
}: Props) {
  // Fast ordning: vänster → höger på den politiska skalan (party.order).
  // Ändras aldrig medan man drar i reglagen.
  const codes = Object.keys(votes).sort((a, b) => getParty(a).order - getParty(b).order)
  const maxVote =
    Math.max(100, ...codes.map((c) => Math.max(votes[c] ?? 0, baseVotes[c] ?? 0))) * 1.4

  return (
    <div className="votes-editor">
      <div className="votes-editor__head">
        <span className="card__title" style={{ margin: 0 }}>
          Röster per parti
        </span>
        <button type="button" className="linkbtn" onClick={onReset}>
          Återställ till utgångsläget
        </button>
      </div>

      {codes.map((code) => {
        const meta = getParty(code)
        const v = votes[code] ?? 0
        const bv = baseVotes[code] ?? 0
        const s = seats[code] ?? 0
        const bs = baseSeats[code] ?? 0
        const seatDelta = s - bs
        return (
          <div key={code} className={`ve-row${code === highlightParty ? ' is-highlight' : ''}`}>
            <span className="pb-chip" style={{ background: meta.color, color: meta.onColor }}>
              {meta.shortName}
            </span>

            <input
              className="ve-range"
              type="range"
              min={0}
              max={Math.round(maxVote)}
              step={1}
              value={v}
              onChange={(e) => onChange(code, Number(e.target.value))}
              style={{ '--party': meta.color } as CSSProperties}
              aria-label={`Röster ${meta.name}`}
            />

            <input
              className="ve-number tnum"
              type="number"
              min={0}
              value={v}
              onChange={(e) => onChange(code, Math.max(0, Number(e.target.value)))}
            />

            <span className="ve-base tnum dim">{v !== bv ? signed(v - bv) : `${int(bv)}`}</span>

            <span className={`ve-seats tnum${seatDelta !== 0 ? ' changed' : ''}`}>
              {s}
              {seatDelta !== 0 && <em>{signed(seatDelta)}</em>}
            </span>
          </div>
        )
      })}
    </div>
  )
}
